(() => {
  const GA4_MEASUREMENT_ID = 'G-JSMYK9Y7Y4';
  const trackedSections = new WeakSet();
  const trackedScrollDepths = new Set();
  const trackedVideoMilestones = new WeakMap();
  const pendingGa4Events = [];
  let ga4FlushTimer = null;

  const cleanText = (value, limit = 120) => String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, limit);

  const pageContext = () => {
    const root = document.documentElement;
    const protocolRoot = document.querySelector('[data-navo-protocol]');
    const articleRoot = document.querySelector('[data-navo-article-title]');
    return {
      page_type: root.dataset.navoPageType || '',
      template: root.dataset.navoTemplate || '',
      template_suffix: root.dataset.navoTemplateSuffix || '',
      protocol: protocolRoot?.dataset.navoProtocol || '',
      protocol_code: protocolRoot?.dataset.navoProtocolCode || '',
      article_title: articleRoot?.dataset.navoArticleTitle || '',
      page_path: window.location.pathname,
      page_title: document.title,
      device_group: window.matchMedia('(max-width: 749px)').matches ? 'mobile' : 'desktop'
    };
  };

  const flushGa4Events = () => {
    ga4FlushTimer = null;
    if (typeof window.gtag !== 'function') {
      if (pendingGa4Events.length) ga4FlushTimer = window.setTimeout(flushGa4Events, 250);
      return;
    }

    while (pendingGa4Events.length) {
      const { eventName, payload } = pendingGa4Events.shift();
      const parameters = { send_to: GA4_MEASUREMENT_ID };
      Object.entries(payload).forEach(([key, value]) => {
        if (key === 'event_timestamp' || value === '' || value === null || value === undefined) return;
        parameters[key] = value;
      });
      window.gtag('event', eventName, parameters);
    }
  };

  const sendToGa4 = (eventName, payload) => {
    pendingGa4Events.push({ eventName, payload });
    if (pendingGa4Events.length > 100) pendingGa4Events.shift();
    if (ga4FlushTimer === null) flushGa4Events();
  };

  const dispatch = (eventName, eventData = {}) => {
    const payload = {
      ...pageContext(),
      ...eventData,
      event_timestamp: new Date().toISOString()
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...payload });
    sendToGa4(eventName, payload);

    try {
      if (window.Shopify?.analytics?.publish) {
        window.Shopify.analytics.publish(eventName, payload);
      }
    } catch (error) {
      if (document.documentElement.dataset.navoTrackingDebug === 'true') console.warn('[NAVO1 tracking] Shopify', error);
    }

    try {
      if (typeof window.clarity === 'function') {
        window.clarity('event', eventName);
        if (payload.protocol) window.clarity('set', 'navo_protocol', payload.protocol);
        if (payload.section_name) window.clarity('set', 'navo_last_section', payload.section_name);
        if (payload.plan_name) window.clarity('set', 'navo_plan', payload.plan_name);
      }
    } catch (error) {
      if (document.documentElement.dataset.navoTrackingDebug === 'true') console.warn('[NAVO1 tracking] Clarity', error);
    }

    document.dispatchEvent(new CustomEvent('navo:track', { detail: { event: eventName, ...payload } }));
    if (document.documentElement.dataset.navoTrackingDebug === 'true') console.info('[NAVO1 tracking]', eventName, payload);
  };

  window.NAVO1Track = dispatch;

  const initPageReady = () => {
    if (document.documentElement.dataset.navoPageTracked === 'true') return;
    document.documentElement.dataset.navoPageTracked = 'true';
    dispatch('navo_page_ready', { referrer_host: (() => {
      try { return document.referrer ? new URL(document.referrer).hostname : ''; } catch { return ''; }
    })() });
  };

  const initSectionViews = (root = document) => {
    const sections = [...root.querySelectorAll('[data-navo-section]')].filter((section) => !trackedSections.has(section));
    if (!sections.length) return;

    const observe = (section) => {
      if (trackedSections.has(section)) return;
      trackedSections.add(section);
      const allSections = [...document.querySelectorAll('[data-navo-section]')];
      const index = allSections.indexOf(section);
      dispatch('navo_section_view', {
        section_name: section.dataset.navoSection,
        section_index: index >= 0 ? index + 1 : null,
        section_count: allSections.length
      });
    };

    if (!('IntersectionObserver' in window)) {
      sections.forEach(observe);
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observe(entry.target);
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '-20% 0px -55% 0px', threshold: 0 });

    sections.forEach((section) => observer.observe(section));
  };

  const initScrollDepth = () => {
    if (document.documentElement.dataset.navoScrollTracked === 'true') return;
    document.documentElement.dataset.navoScrollTracked = 'true';
    const milestones = [25, 50, 75, 90, 100];
    let scheduled = false;

    const calculate = () => {
      scheduled = false;
      const scrollable = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      const depth = Math.min(100, Math.round((window.scrollY / scrollable) * 100));
      milestones.forEach((milestone) => {
        if (depth < milestone || trackedScrollDepths.has(milestone)) return;
        trackedScrollDepths.add(milestone);
        dispatch('navo_scroll_depth', { scroll_percent: milestone });
      });
    };

    window.addEventListener('scroll', () => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(calculate);
    }, { passive: true });
    calculate();
  };

  const initCarouselTracking = (root = document) => {
    root.querySelectorAll('[data-navo-carousel]:not([data-tracking-ready])').forEach((carousel) => {
      carousel.dataset.trackingReady = 'true';
      const sectionName = carousel.closest('[data-navo-section]')?.dataset.navoSection || 'carousel';
      const slides = [...carousel.querySelectorAll('[data-carousel-slide]')];
      const trackInitial = () => dispatch('navo_carousel_view', {
        carousel_name: sectionName,
        slide_index: 1,
        slide_count: slides.length,
        slide_label: cleanText(slides[0]?.dataset.carouselLabel || slides[0]?.querySelector('img')?.alt || '', 100)
      });

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          trackInitial();
          observer.disconnect();
        }, { rootMargin: '-20% 0px -55% 0px', threshold: 0 });
        observer.observe(carousel);
      } else {
        trackInitial();
      }

      carousel.addEventListener('navo:carousel-change', (event) => {
        const index = Number(event.detail?.index || 0);
        const slide = slides[index];
        dispatch('navo_carousel_change', {
          carousel_name: sectionName,
          slide_index: index + 1,
          slide_count: slides.length,
          slide_label: cleanText(slide?.dataset.carouselLabel || slide?.querySelector('img')?.alt || '', 100),
          navigation_source: event.detail?.source || 'unknown'
        });
      });
    });
  };

  const initPlanTracking = (root = document) => {
    root.querySelectorAll('[data-navo-plan]:not([data-plan-tracking-ready])').forEach((plan, index) => {
      plan.dataset.planTrackingReady = 'true';
      const track = (interaction) => dispatch('navo_plan_select', {
        plan_index: index + 1,
        plan_name: plan.dataset.planTitle || '',
        plan_duration: plan.dataset.planDuration || '',
        plan_quantity: plan.dataset.planQuantity || '',
        plan_price: plan.dataset.planPrice || '',
        plan_saving: plan.dataset.planSaving || '',
        interaction
      });
      plan.addEventListener('click', () => track('click'));
      plan.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') track('keyboard');
      });
    });
  };

  const initPurchaseTracking = (root = document) => {
    root.querySelectorAll('[data-navo-purchase-form]:not([data-purchase-tracking-ready])').forEach((form) => {
      form.dataset.purchaseTrackingReady = 'true';
      form.addEventListener('submit', (event) => {
        const variantId = form.querySelector('[data-navo-purchase-variant]')?.value || '';
        if (!variantId) {
          event.preventDefault();
          return;
        }
        dispatch('navo_add_to_cart_attempt', {
          plan_name: form.dataset.selectedPlan || '',
          plan_duration: form.dataset.selectedDuration || '',
          plan_price: form.dataset.selectedPrice || '',
          product_handle: form.dataset.selectedProductHandle || '',
          variant_id: variantId
        });
      });
    });
  };

  const initCtaTracking = (root = document) => {
    root.querySelectorAll('[data-navo-scroll-cta]:not([data-cta-tracking-ready]), [data-navo-content-cta]:not([data-cta-tracking-ready])').forEach((cta) => {
      cta.dataset.ctaTrackingReady = 'true';
      cta.addEventListener('click', () => dispatch('navo_cta_click', {
        cta_name: cta.dataset.navoScrollCta || cta.dataset.navoContentCta || cleanText(cta.textContent, 80),
        cta_label: cleanText(cta.textContent, 80),
        cta_target: cta.getAttribute('href') || '',
        section_name: cta.closest('[data-navo-section]')?.dataset.navoSection || ''
      }));
    });
  };

  const initFaqTracking = (root = document) => {
    root.querySelectorAll('[data-navo-section="faq"] details:not([data-faq-tracking-ready])').forEach((details, index) => {
      details.dataset.faqTrackingReady = 'true';
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        dispatch('navo_faq_open', {
          faq_index: index + 1,
          faq_question: cleanText(details.querySelector('summary')?.textContent, 140)
        });
      });
    });
  };

  const initProgressTracking = (root = document) => {
    root.querySelectorAll('[data-navo-progress]:not([data-progress-tracking-ready])').forEach((journey) => {
      journey.dataset.progressTrackingReady = 'true';
      journey.addEventListener('navo:progress-open', (event) => {
        dispatch('navo_progress_week_open', {
          progress_week: event.detail?.week || '',
          open_source: event.detail?.source || 'unknown'
        });
      });
    });
  };

  const initVideoTracking = (root = document) => {
    root.querySelectorAll('[data-navo-autoplay-video]:not([data-video-tracking-ready])').forEach((wrapper) => {
      const video = wrapper.querySelector('video');
      if (!video) return;
      wrapper.dataset.videoTrackingReady = 'true';
      const name = wrapper.dataset.videoName || wrapper.closest('[data-navo-section]')?.dataset.navoSection || 'video';
      trackedVideoMilestones.set(video, new Set());

      video.addEventListener('play', () => {
        const milestones = trackedVideoMilestones.get(video);
        if (milestones.has('start')) return;
        milestones.add('start');
        dispatch('navo_video_start', { video_name: name });
      });
      video.addEventListener('timeupdate', () => {
        if (!video.duration) return;
        const progress = Math.round((video.currentTime / video.duration) * 100);
        [25, 50, 75].forEach((milestone) => {
          const milestones = trackedVideoMilestones.get(video);
          if (progress < milestone || milestones.has(milestone)) return;
          milestones.add(milestone);
          dispatch('navo_video_progress', { video_name: name, video_percent: milestone });
        });
      });
      video.addEventListener('ended', () => dispatch('navo_video_complete', { video_name: name }));
      wrapper.querySelector('[data-video-toggle]')?.addEventListener('click', () => {
        window.setTimeout(() => dispatch('navo_video_control', {
          video_name: name,
          control_action: video.paused ? 'pause' : 'play'
        }), 0);
      });
    });
  };

  const initContactTracking = (root = document) => {
    const form = root.querySelector('#ContactForm:not([data-contact-tracking-ready])');
    if (form) {
      form.dataset.contactTrackingReady = 'true';
      form.addEventListener('submit', () => dispatch('navo_contact_submit', { form_name: 'contact' }));
    }
    if (root.querySelector('[data-navo-contact-success]') && document.documentElement.dataset.navoContactSuccessTracked !== 'true') {
      document.documentElement.dataset.navoContactSuccessTracked = 'true';
      dispatch('navo_contact_success', { form_name: 'contact' });
    }
  };

  const initArticleTracking = (root = document) => {
    root.querySelectorAll('[data-navo-article-card]:not([data-article-tracking-ready])').forEach((card) => {
      card.dataset.articleTrackingReady = 'true';
      card.querySelectorAll('a[href]').forEach((link) => {
        link.addEventListener('click', () => dispatch('navo_article_open', {
          article_title: cleanText(card.dataset.articleTitle || link.textContent, 140),
          article_url: link.getAttribute('href') || ''
        }));
      });
    });
    root.querySelectorAll('[data-navo-article-link]:not([data-article-tracking-ready])').forEach((link) => {
      link.dataset.articleTrackingReady = 'true';
      link.addEventListener('click', () => dispatch('navo_article_open', {
        article_title: cleanText(link.dataset.articleTitle || link.textContent, 140),
        article_url: link.getAttribute('href') || ''
      }));
    });
  };

  const initAll = (root = document) => {
    initPageReady();
    initSectionViews(root);
    initScrollDepth();
    initCarouselTracking(root);
    initPlanTracking(root);
    initPurchaseTracking(root);
    initCtaTracking(root);
    initFaqTracking(root);
    initProgressTracking(root);
    initVideoTracking(root);
    initContactTracking(root);
    initArticleTracking(root);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  else initAll();

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
